
import { handleTableId } from "../../../../helpers/helper"
import { FinancingRowChip } from "../../../Contact/Financing/FinancingChip"
import React, { useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { changeStatus, getLeadDispositions, changeLeadDisposition } from "../../../../features/customer/customerSlice"
import { showSnackBar } from "../../../../components/Snackbar/Snackbar"
import { ImportedIcon } from "../../../../components/Icons/Icons"
import { LuChevronRight, LuPencil, LuTrash2 } from "react-icons/lu"
import { NavLink } from "react-router-dom"
import ConfirmModal from "../../../../components/ConfirmModal/ConfirmModal"
import UserStatusCell from "./StatusCell"
import { phoneNumber, formatCurrency } from "../../../../utils/payment"
import { useMyContextLoading } from "../../../../components/LoadingContextProvider/LoadingContextProvider"
import { StorageGetItem, USER } from "../../../../lib/storage"
import PopupWithActions from "../../../../components/Popup/PopupWithAction"
import { formatUserWithContactGroups } from "../../../../utils/assignees"
import { formatFullDate, formatDateField } from "../../../../utils/date"
import SortableTableHeader from "../../../../components/Table/SortableTableHeader"
import TableSkeletonRows from "../../../../components/Skeleton/TableSkeletonRows"
import { getColumns } from "./columns"
import NotFoundData from "../NotFoundData"
import { TABLE_HEAD_ROW } from "../../../../components/Table/tableCardClass"
import { DEMOTION_GUARD_FLAG, RESTORE_SKIP_TEXT } from "../../../../constants/demotionGuard"
import { selectFeatureFlag } from "../../../../features/featureFlags/selectors"
import { CONTACT_ACTIVE_DEDUP_BY_SSN, LEAD_DISPOSITION_PIPELINE } from "../../../../config/featureFlagKeys"
import StatusChangeConfirm from "../../../../components/StatusChangeConfirm/StatusChangeConfirm"
import LeadStatusConfirm from "../../../../components/LeadStatus/LeadStatusConfirm"
import LeadStatusChip from "../../../../components/LeadStatus/LeadStatusChip"
import { changeDispositionOutcome } from "../../../../components/LeadStatus/leadStatus"

const TableContactList = ({
  data,
  isContacts = false,
  statuses,
  triggerUpdate = false,
  sortField = '',
  sortOrder = '',
  onSort,
  contactGroupsMap = {},
  usersMap = {},
  loading = false,
  skeletonRows = 10,
  showDebtAmount = false,
}) => {
  const dispatch = useDispatch()
  const demotionGuardOn = useSelector(selectFeatureFlag(DEMOTION_GUARD_FLAG))
  const activeDedupOn = useSelector(selectFeatureFlag(CONTACT_ACTIVE_DEDUP_BY_SSN))
  const pipelineOn = useSelector(selectFeatureFlag(LEAD_DISPOSITION_PIPELINE))
  const { triggerLoading, isBarLoading } = useMyContextLoading()
  const showSkeleton = (loading || isBarLoading) && !(data?.length > 0)

  // Get column configuration based on mode
  const columns = getColumns(isContacts, { showDebtAmount })
  const [anchorStatuses, setAnchorStatuses] = useState(null)
  const [userId, setUserId] = useState('')
  const [openChangeStatusModal, setOpenChangeStatusModal] = useState(false)
  const [openStatuses, setOpenStatuses] = useState(false)
  const [changeStatusId, setChangeStatusId] = useState(false)
  const [userRole, setUserRole] = useState('')
  const [dispositions, setDispositions] = useState([])
  const [anchorDispositions, setAnchorDispositions] = useState(null)
  const [openDispositionsId, setOpenDispositionsId] = useState(null)
  const [openChangeDispositionModal, setOpenChangeDispositionModal] = useState(false)
  const [changeDispositionId, setChangeDispositionId] = useState(null)
  const [dispositionUserId, setDispositionUserId] = useState('')

  const canEditRow = !!userRole && !(userRole === 'affiliate' && isContacts)

  useEffect(() => {
    StorageGetItem(USER).then(result => {
      setUserRole(result?.role)
    })
    if (!isContacts) {
      dispatch(getLeadDispositions()).then(res => {
        if (res?.payload?.data) setDispositions(res.payload.data)
      })
    }
  }, [isContacts])

  const handleClickDispositions = (event, id) => {
    if (openDispositionsId === id) {
      setOpenDispositionsId(null)
      setAnchorDispositions(null)
    } else {
      setOpenDispositionsId(id)
      setAnchorDispositions(event.currentTarget)
      setDispositionUserId(id)
    }
  }

  const handleCloseDispositions = () => {
    setOpenDispositionsId(null)
    setAnchorDispositions(null)
  }

  const handleChangeDisposition = async () => {
    triggerLoading(true)
    try {
      await dispatch(changeLeadDisposition({ userId: dispositionUserId, dispositionId: changeDispositionId }))
      showSnackBar("success", "Disposition successfully changed!")
      trigger()
    } catch (error) {
      showSnackBar('error', 'An unexpected error occurred')
    }
    triggerLoading(false)
  }

  const handleChangeLeadStatus = async ({ reason } = {}) => {
    triggerLoading(true)
    try {
      const result = await dispatch(changeLeadDisposition({ userId: dispositionUserId, dispositionId: changeDispositionId, reason }))
      const outcome = changeDispositionOutcome(result)
      showSnackBar(outcome.ok ? 'success' : 'error', outcome.message)
      trigger()
    } catch (error) {
      showSnackBar('error', 'An unexpected error occurred')
    }
    triggerLoading(false)
  }

  useEffect(() => {
    const handleDocumentClick = (event) => {
      if (anchorStatuses && !anchorStatuses.contains(event.target)) {
        setOpenStatuses(null)
        setAnchorStatuses(null)
      }
    }

    document.addEventListener('click', handleDocumentClick)

    return () => {
      document.removeEventListener('click', handleDocumentClick)
    }
  }, [anchorStatuses])

  const trigger = () => {
    if (typeof triggerUpdate === 'function') {
      triggerUpdate(true)
    }
  }

  const handleClickStatuses = (event, id) => {
    if (id === openStatuses) {
      setOpenStatuses(null)
      setAnchorStatuses(null)
    } else {
      setOpenStatuses(id)
      setAnchorStatuses(event.currentTarget)
      setUserId(id)
    }
  }

  const handleChangeStatus = async () => {
    triggerLoading(true)
    try {
      const result = await dispatch(changeStatus({ statusId: changeStatusId, userId }))
      if (activeDedupOn && result?.error) {
        showSnackBar("error", typeof result.payload === 'string' ? result.payload : 'Customer status could not be changed.')
      } else {
        showSnackBar("success", "Customer status successfully changed!")
      }
      trigger()
    } catch (error) {
      showSnackBar('error', 'An unexpected error occurred:')
    }
    triggerLoading(false)
  }

  const handleGuardedChangeStatus = async (body) => {
    triggerLoading(true)
    try {
      const result = await dispatch(changeStatus({ statusId: changeStatusId, userId, ...(body || {}) }))
      if (result?.error) {
        showSnackBar("error", typeof result.payload === 'string' ? result.payload : 'Customer status could not be changed.')
      } else {
        const planRestore = result?.payload?.data?.planRestore
        let message = "Customer status successfully changed!"
        if (planRestore) {
          message += planRestore.restored
            ? " Billing resumed."
            : ` Billing was not resumed: ${RESTORE_SKIP_TEXT[planRestore.reason] || 'check Payment Plan'}.`
        }
        showSnackBar("success", message)
      }
      trigger()
    } catch (error) {
      showSnackBar('error', 'An unexpected error occurred:')
    }
    triggerLoading(false)
  }

  const pickedStatus = statuses?.find((status) => status?._id === changeStatusId)

  const handleCloseStatuses = () => {
    setOpenStatuses(false)
  }

  return (
    <div>
      {openChangeStatusModal && demotionGuardOn &&
        <StatusChangeConfirm
          open={openChangeStatusModal}
          setOpen={setOpenChangeStatusModal}
          userId={userId}
          status={pickedStatus}
          onConfirm={handleGuardedChangeStatus} />}
      {openChangeStatusModal && !demotionGuardOn &&
        <ConfirmModal leftTopIcon={<LuTrash2 size={18} className="text-text-secondary" />}
          className="min-w-[400px] min-h-[200px]"
          title="Warning!"
          modalButtonTitle={"Change"} open={openChangeStatusModal}
          setOpen={setOpenChangeStatusModal}
          functionButton={handleChangeStatus}>
          Are you sure to change status?
        </ConfirmModal>}
      <div className="overflow-x-auto">
        <table className="text-md text-left w-full text-text-primary min-w-max border-collapse [&_td]:align-middle [&_th]:align-middle">
          <thead className="w-full">
            <tr className={`${TABLE_HEAD_ROW} px-10`}>
              {columns.map((col) => (
                col.sortable ? (
                  <SortableTableHeader
                    key={col.key}
                    label={col.label}
                    field={col.field}
                    currentSortField={sortField}
                    currentSortOrder={sortOrder}
                    onSort={onSort}
                  />
                ) : (
                  <th key={col.key} className="pl-5 py-4 font-semibold text-sm uppercase">
                    {col.label}
                  </th>
                )
              ))}
              <th className="pl-6 text-left font-semibold text-sm uppercase">Action</th>
            </tr>
          </thead>
          <tbody>
            {showSkeleton && <TableSkeletonRows rows={skeletonRows} colSpan={columns.length + 1} />}
            {/* Without this the tbody rendered nothing at all on a zero-result
                filter — a header row over blank space, with no way to tell
                whether it was still loading or genuinely empty. */}
            {!showSkeleton && data?.length === 0 && (
              <tr>
                <td colSpan={columns.length + 1} className="p-0 border-none">
                  <NotFoundData />
                </td>
              </tr>
            )}
            {!showSkeleton && data?.map((element, i) => (
              <React.Fragment key={i}>
                <tr className="border-b border-divider hover:bg-black/[0.04] dark:hover:bg-white/[0.04] transition-colors">
                  <td>
                    <div className="flex flex-row items-center">
                      <div className="py-4 pl-5 text-primary-main font-semibold cursor-pointer">
                        <NavLink to={`/contact/${element._id}`} className="cursor-pointer">
                          {handleTableId(element._id)}
                        </NavLink>
                      </div>
                      {isContacts && element?.isFavorite && (
                        <div
                          className="ml-2 rounded-full bg-primary-static-blue-700"
                          style={{ width: '8px', height: '8px' }}
                          title="Favorite"
                        />
                      )}
                      {element.createdWithApiKey && <span title="Created With API Key"><ImportedIcon /></span>}
                    </div>
                    {/* WS 2311820: set by the server only behind CONTACT_FINANCING_PENDING. */}
                    {element.financingState && (
                      <div className="pl-5 -mt-3 pb-3"><FinancingRowChip financingState={element.financingState} /></div>
                    )}
                  </td>
                  <td className="pl-5 py-4">{element.firstName}</td>
                  <td className="pl-5 py-4">{element.lastName}</td>
                  <td
                    className="pl-5 py-4 max-w-[200px] overflow-hidden text-ellipsis break-words">{element?.secondaryEmail}</td>
                  <td className="pl-5 py-4">{phoneNumber(element.primaryPhone) || 'N/A'}</td>
                  <td className="pl-5 py-4">
                    {element?.createdAt ? formatFullDate(element?.createdAt) : 'N/A'}
                  </td>
                  {isContacts && (
                    <>
                      <td className="pl-5 py-4">
                        {element?.dateOfBirth ? formatDateField(element?.dateOfBirth) : 'N/A'}
                      </td>
                      <td className="pl-5 py-4">
                        {element?.enrollmentDate ? formatDateField(element?.enrollmentDate) : 'N/A'}
                      </td>
                      <td className="pl-5 py-4">
                        {element?.canceledDate ? formatDateField(element?.canceledDate) : 'N/A'}
                      </td>
                      <td className="pl-5 py-4">
                        {element?.nextPayment?.dueDate ? formatDateField(element?.nextPayment?.dueDate) : 'N/A'}
                      </td>
                      <td className="pl-5 py-4">
                        {element?.plan?.amount ? formatCurrency(element?.plan?.amount) : 'N/A'}
                      </td>
                      <td className="pl-5 py-4">
                        {element?.totalDebtEnrolled != null ? formatCurrency(element.totalDebtEnrolled) : 'N/A'}
                      </td>
                      <td className="pl-5 py-4">
                        {element?.programLength != null ? `${element.programLength} payments` : 'N/A'}
                      </td>
                      <td className="pl-5 py-4">
                        <span className={`px-2 py-1 rounded text-xs ${element?.welcomeCallCompleted
                          ? 'bg-success-light text-success-main'
                          : 'bg-[var(--color-gray-100)] dark:bg-white/[0.06] text-text-secondary'
                          }`}>
                          {element?.welcomeCallCompleted ? 'Completed' : 'Pending'}
                        </span>
                      </td>
                      <td className="pl-5 py-4">
                        <span className={`px-2 py-1 rounded text-xs ${element?.plan?.status === 'active'
                            ? 'bg-success-light text-success-main'
                            : 'bg-[var(--color-gray-100)] dark:bg-white/[0.06] text-text-secondary'
                          }`}>
                          {element?.plan?.status === 'active' ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                    </>
                  )}
                  {!isContacts && (
                    <td className="pl-5 py-4">
                      {pipelineOn && !element?.dispositionData?._id ? (
                        <LeadStatusChip onClick={(event) => handleClickDispositions(event, element._id)} />
                      ) : (
                        element?.dispositionData && (
                          <div className="cursor-pointer"
                            onClick={(event) => handleClickDispositions(event, element._id)}>
                            <UserStatusCell
                              statusObj={element?.dispositionData}
                              arrow={true}
                              isOpen={openDispositionsId === element._id}
                              outlined
                            />
                          </div>
                        )
                      )}
                    </td>
                  )}
                  {!isContacts && showDebtAmount && (
                    <td className="pl-5 py-4">
                      {element?.totalDebtSelected != null ? formatCurrency(element.totalDebtSelected) : 'N/A'}
                    </td>
                  )}
                  <td className="pl-5 py-4 max-w-[150px] overflow-hidden text-ellipsis break-words">
                    {element?.acquisitionSource || ''}
                  </td>
                  <td className="pl-5 py-4">
                    <div className="cursor-pointer"
                      onClick={(event) => handleClickStatuses(event, element._id)}>
                      <UserStatusCell statusObj={element?.status} />
                    </div>
                  </td>
                  <td className="pl-5 py-4 max-w-[150px] overflow-hidden text-ellipsis break-words">
                    {formatUserWithContactGroups({ user: element?.creator, userRole, contactGroupsMap })}
                  </td>
                  {isContacts && (
                    <td className="pl-5 py-4">
                      <div className="flex flex-col max-w-[150px] overflow-hidden text-ellipsis break-words">
                        {element?.assignees?.length > 0
                          ? element?.assignees?.map(assigneeId => {
                              const assignee = usersMap[assigneeId]
                              return assignee ? (
                                <span
                                  key={assigneeId}
                                  className="truncate overflow-hidden text-ellipsis whitespace-normal line-clamp-2"
                                >
                                  {formatUserWithContactGroups({ user: assignee, userRole, contactGroupsMap })}
                                </span>
                              ) : null
                            })
                          : (
                              <span className="truncate whitespace-nowrap overflow-hidden text-ellipsis">
                                {element?.creator?.firstName + ' ' + element?.creator?.lastName}
                              </span>
                            )}
                      </div>
                    </td>
                  )}
                  <td className="pl-5 pr-8 py-4">
                    <div className="flex justify-end items-center gap-3">
                      {canEditRow &&
                        <NavLink to={isContacts ? `/contact/edit/${element._id}` : `/lead/edit/${element._id}`}>
                          <div className="cursor-pointer text-text-secondary hover:text-primary-main">
                            <LuPencil size={18} />
                          </div>
                        </NavLink>
                      }
                      <NavLink to={`/contact/${element._id}`} className="cursor-pointer text-text-secondary hover:text-primary-main">
                        <LuChevronRight size={20} />
                      </NavLink>
                    </div>
                  </td>

                </tr>
                {statuses && statuses?.length > 0 && userRole !== 'affiliate' && <tr>
                  <td colSpan={columns?.length + 1}>
                    <PopupWithActions
                      shadow="shadow-sm"
                      anchor={anchorStatuses}
                      open={openStatuses}
                      handleClose={handleCloseStatuses}
                    >
                      {
                        statuses?.map((status, key) => (
                          <div
                            key={key}
                            onClick={() => {
                              setOpenChangeStatusModal(true)
                              setChangeStatusId(status?._id)
                            }
                            }
                            className="cursor-pointer p-3.5 rounded-md text-text-primary hover:bg-black/[0.04] dark:hover:bg-white/[0.04]"
                          >
                            {status?.label}
                          </div>
                        ))
                      }
                    </PopupWithActions>
                  </td>
                </tr>}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
      {!isContacts && dispositions.length > 0 && (
        <PopupWithActions
          shadow="shadow-sm"
          anchor={anchorDispositions}
          open={openDispositionsId !== null}
          handleClose={handleCloseDispositions}
        >
          {dispositions.map((disp, key) => (
            <div
              key={key}
              onClick={() => {
                setOpenChangeDispositionModal(true)
                setChangeDispositionId(disp._id)
                handleCloseDispositions()
              }}
              className="cursor-pointer p-3.5 rounded-md text-text-primary hover:bg-black/[0.04] dark:hover:bg-white/[0.04]"
            >
              {disp.label}
            </div>
          ))}
        </PopupWithActions>
      )}
      {openChangeDispositionModal && pipelineOn && (
        <LeadStatusConfirm
          open={openChangeDispositionModal}
          setOpen={setOpenChangeDispositionModal}
          status={dispositions.find(d => d._id === changeDispositionId)}
          onConfirm={handleChangeLeadStatus} />
      )}
      {openChangeDispositionModal && !pipelineOn && (
        <ConfirmModal
          className="min-w-[400px] min-h-[200px]"
          title="Change Lead Status"
          modalButtonTitle="Change"
          open={openChangeDispositionModal}
          setOpen={setOpenChangeDispositionModal}
          functionButton={handleChangeDisposition}>
          Are you sure you want to change the lead status?
        </ConfirmModal>
      )}
    </div>
  )
}

export default TableContactList
